import React, { useState, useEffect, lazy, Suspense } from 'react'
import { Button, Layout, Row, Col, Skeleton, Result, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import YearFilter from './components/year-filter'
import MonthFilter from './components/month-filter'
import ResourceFilter from './components/resource-filter'
import DepartmentFilter from './components/department-filter'
import VacancyFilter from './components/vacancy-filter'
import ManagerFilter from './components/manager-filter'
import B2BLayout from '../../layouts/b2b'

const Statistics = lazy(() => import('./components/statistics'))
const CancelReasonsPieChart = lazy(() => import('./components/cancelreasonspiechart'))
import {
  MapTitle,
  MapWrapper,
  TaskWrapper,
  FiltersContainer,
  TableContainer,
  StatisticsCardT,
} from './styles'
import { IStatisticsProps } from './interfaces/interfaces'
import { useFetchData } from './hooks/useFetchData'
import { monthOrder } from './constants'

const { Content, Sider } = Layout

const breadcrumbs = [
  {
    href: '/hr',
    title: 'Отдел персонала',
  },
  {
    title: 'Динамика действий по месяцам',
  },
]

export default function AdminNewObjectPage() {
  const navigate = useNavigate()
  const [selectedYears, setSelectedYears] = useState<string[]>([])
  const [selectedMonths, setSelectedMonths] = useState<string[]>([])
  const [selectedResources, setSelectedResources] = useState<string[]>([])
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([])
  const [selectedVacancies, setSelectedVacancies] = useState<string[]>([])
  const [selectedManagers, setSelectedManagers] = useState<string[]>([])
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true)

  const {
    stagesData,
    years,
    resources,
    departments,
    vacancies,
    managers,
    loading,
    cancelReasons,
    arResultVacancy,
    error,
  } = useFetchData(
    selectedYears,
    selectedMonths,
    selectedResources,
    selectedDepartments,
    selectedVacancies,
    selectedManagers,
  )

  useEffect(() => {
    if (years.length > 0 && isFirstLoad) {
      const currentYear = new Date().getFullYear().toString()
      setSelectedYears([currentYear])
      setIsFirstLoad(false)
    }
  }, [years, isFirstLoad])

  const handleYearChange = (years: string[]) => {
    setSelectedYears(years)
  }

  const handleMonthChange = (months: string[]) => {
    setSelectedMonths(months)
  }

  const handleResourceChange = (resources: string[]) => {
    setSelectedResources(resources)
  }

  const handleDepartmentChange = (departments: string[]) => {
    setSelectedDepartments(departments)
  }

  const handleVacancyChange = (vacancies: string[]) => {
    setSelectedVacancies(vacancies)
  }

  const handleManagerChange = (managers: string[]) => {
    setSelectedManagers(managers)
  }

  const handlePaymentsClick = () => {
    navigate('/hr/payments')
  }

  const handleClearAllFilters = () => {
    const currentYear = new Date().getFullYear().toString()
    setSelectedYears([currentYear])
    setSelectedMonths([])
    setSelectedResources([])
    setSelectedDepartments([])
    setSelectedVacancies([])
    setSelectedManagers([])
    setSelectedManagers([])
  }

  const totalResponses = stagesData.reduce((acc, data) => acc + data.total, 0)
  const totalInterviews = stagesData.reduce((acc, data) => acc + data.interview, 0)
  const totalRejected = stagesData.reduce((acc, data) => acc + data.rejected, 0)
  const totalAccepted = stagesData.reduce((acc, data) => acc + data.accepted, 0)

  const statistics: IStatisticsProps = {
    totalResponses,
    totalAccepted,
    totalRejected,
    totalInterviews,
    stagesData,
    loading,
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && <Skeleton active />}
      {!loading && error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!loading && !error && (
        <MapWrapper>
          <TaskWrapper>
            <MapTitle>Динамика действий по месяцам</MapTitle>
            <Button type="primary" onClick={handlePaymentsClick}>
              Оплаты
            </Button>
          </TaskWrapper>
          <Layout>
            <Content style={{ padding: '20px' }}>
              <Suspense
                fallback={<Spin size="large" style={{ display: 'block', margin: '24px auto' }} />}
              >
                <Statistics {...statistics} />
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <StatisticsCardT style={{ overflow: 'hidden' }}>
                      <TableContainer style={{ overflow: 'hidden' }}>
                        {loading ? (
                          <Skeleton active />
                        ) : (
                          <CancelReasonsPieChart
                            cancelReasons={cancelReasons}
                            arResultVacancy={arResultVacancy}
                          />
                        )}
                      </TableContainer>
                    </StatisticsCardT>
                  </Col>
                </Row>
              </Suspense>
            </Content>
            <Sider width={300} style={{ background: '#f0f2f5', padding: '20px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <h3>Фильтры</h3>
                <Button onClick={handleClearAllFilters} type="default">
                  Очистить все
                </Button>
              </div>
              <FiltersContainer>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <YearFilter
                      selectedYears={selectedYears}
                      onYearChange={handleYearChange}
                      years={years}
                    />
                  </Col>
                  <Col span={24}>
                    <MonthFilter
                      selectedMonths={selectedMonths}
                      onMonthChange={handleMonthChange}
                      months={monthOrder}
                    />
                  </Col>
                  <Col span={24}>
                    <ResourceFilter
                      selectedResources={selectedResources}
                      onResourceChange={handleResourceChange}
                      resources={resources}
                    />
                  </Col>
                  <Col span={24}>
                    <DepartmentFilter
                      selectedDepartments={selectedDepartments}
                      onDepartmentChange={handleDepartmentChange}
                      departments={departments}
                    />
                  </Col>
                  <Col span={24}>
                    <VacancyFilter
                      selectedVacancies={selectedVacancies}
                      onVacancyChange={handleVacancyChange}
                      vacancies={vacancies}
                    />
                  </Col>
                  <Col span={24}>
                    <ManagerFilter
                      selectedManagers={selectedManagers}
                      onManagerChange={handleManagerChange}
                      managers={managers}
                    />
                  </Col>
                </Row>
              </FiltersContainer>
            </Sider>
          </Layout>
        </MapWrapper>
      )}
    </B2BLayout>
  )
}
